import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import BaseModal from '../../ui/BaseModal';
import {
  X,
  User,
  Search,
  MessageSquare,
  Plus,
  ChevronDown,
  Check,
} from "lucide-react";

const StartConversationModal = ({ isOpen, onClose, onStartConversation }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState("");

  // Load clients when modal opens
  useEffect(() => {
    if (isOpen) {
      loadClients();
    }
  }, [isOpen]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/clients`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        const clientsData = data.data || data.clients || data || [];
        setClients(clientsData);
      }
    } catch (error) {
      console.error("Error loading clients:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter clients based on search query
  const filteredClients = clients.filter(
    (client) =>
      client.fullname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleStartConversation = () => {
    if (selectedClient && initialMessage.trim()) {
      onStartConversation({
        client: selectedClient,
        message: initialMessage.trim(),
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedClient(null);
    setSearchQuery("");
    setInitialMessage("");
    setIsDropdownOpen(false);
    onClose();
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setSearchQuery(client.fullname || client.email);
    setIsDropdownOpen(false);
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="lg" showClose={false}>
      <div className="p-5 overflow-y-auto max-h-[75vh]">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Start New Conversation
                  </h2>
                  <p className="text-sm text-gray-600">
                    Begin a chat with one of your clients
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Client Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select Client
                </label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setIsDropdownOpen(true);
                        if (!e.target.value) {
                          setSelectedClient(null);
                        }
                      }}
                      onFocus={() => setIsDropdownOpen(true)}
                      placeholder="Search clients by name or email..."
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          isDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>

                  {/* Dropdown */}
                  {isDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {loading ? (
                        <div className="p-4 text-center text-gray-500">
                          <div className="inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="ml-2">Loading clients...</span>
                        </div>
                      ) : filteredClients.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          <User className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p>No clients found</p>
                        </div>
                      ) : (
                        filteredClients.map((client) => (
                          <button
                            key={client.clientid}
                            onClick={() => handleClientSelect(client)}
                            className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                              selectedClient?.clientid === client.clientid
                                ? "bg-blue-50 border-l-4 border-blue-500"
                                : ""
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {client.fullname || "Unknown Client"}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {client.email || "No email"}
                                </p>
                              </div>
                              {selectedClient?.clientid === client.clientid && (
                                <Check className="w-4 h-4 text-blue-600" />
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </motion.div>
                  )}
                </div>

                {/* Selected Client Display */}
                {selectedClient && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-blue-50 border border-blue-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">
                          {selectedClient.fullname}
                        </p>
                        <p className="text-sm text-blue-700">
                          {selectedClient.email}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Initial Message */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Initial Message
                </label>
                <textarea
                  value={initialMessage}
                  onChange={(e) => setInitialMessage(e.target.value)}
                  placeholder="Type your first message to start the conversation..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500">
                  This will be the first message sent to the client
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStartConversation}
                disabled={!selectedClient || !initialMessage.trim()}
                className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4" />
                Start Conversation
              </button>
            </div>
      </div>
    </BaseModal>
  );
};

export default StartConversationModal;
